const isDebugEnabled =
	process.env.NODE_ENV !== 'production' ||
	process.env.VUE_APP_INTERCOM_DEBUG === 'true'

let bootMode = null
let bootedUserId = null
let bootedUserHash = null
let lastIdentifiedSettings = null

function log(action, meta = {}) {
	if (!isDebugEnabled) return
	console.info('[Intercom]', action, meta)
}

function getIntercom() {
	return typeof window !== 'undefined' ? window.Intercom : undefined
}

export function getBaseSettings() {
	return {
		api_base:
			process.env.VUE_APP_INTERCOM_API || 'https://api-iam.intercom.io',
		app_id: process.env.VUE_APP_INTERCOM_ID || 'z1oijek0',
	}
}

export function applySettings(settings) {
	const merged = { ...getBaseSettings(), ...settings }
	if (!merged.app_id) {
		log('invalid-settings', { cause: 'missing-app-id' })
		return null
	}
	if (typeof window !== 'undefined') {
		window.intercomSettings = merged
	}
	return merged
}

export function shutdown(reason = 'unknown') {
	const ic = getIntercom()
	if (ic) {
		ic('shutdown')
	}
	bootMode = null
	bootedUserId = null
	bootedUserHash = null
	lastIdentifiedSettings = null
	log('shutdown', { reason })
}

function buildRoles(user) {
	const roles = []
	for (const key in user.roles || {}) {
		const r = user.roles[key]
		if (
			r.is_partner_role &&
			(r.name === 'office_manager' || r.name === 'partner-admin')
		) {
			!roles.includes('manager') && roles.push('manager')
		}
	}
	for (const key in user.providers || {}) {
		const p = user.providers[key]
		if (p.active && p.user_id === user.id) {
			!roles.includes('provider') && roles.push('provider')
		}
	}
	return roles
}

export function buildIdentifiedSettings(user) {
	const partners = user.partners || []
	const mapPartnerToCompany = partner => ({
		company_id: partner.id,
		name: partner.name,
		industry: 'Healthcare',
		custom_attributes: partner?.intercom_attributes || {},
	})
	const company = partners.find(partner => {
		return String(partner.id) === String(user.primary_partner_id)
	})

	return {
		user_id: user.id,
		name: `${user.first_name} ${user.last_name}`,
		email: user.email,
		clarus_role: buildRoles(user).toString(),
		user_hash: user.intercom_hash_web,
		...(company && {
			custom_attributes: {
				primary_company_id: company.id,
				primary_company_name: company.name,
				primary_company_office_hours_set: company.office_hours_set,
				primary_company_multiple_ivr_partner:
					company.multiple_ivr_partner,
				primary_company_multiple_ivr_assigned_single_number:
					company.multiple_ivr_assigned_single_number,
				...(user.primary_company_role && {
					primary_company_role: user.primary_company_role,
				}),
			},
		}),
		companies: partners.map(mapPartnerToCompany),
	}
}

export function bootAnonymous(reason = 'unknown') {
	const ic = getIntercom()
	if (!ic) {
		log('boot-anonymous-skipped', { reason, cause: 'no-intercom' })
		return
	}

	shutdown('before-anonymous-boot')
	const settings = applySettings({})
	if (!settings) return

	ic('boot', settings)
	bootMode = 'anonymous'
	bootedUserId = null
	bootedUserHash = null
	lastIdentifiedSettings = null
	log('boot-anonymous', { reason })
}

export function bootIdentified(user, reason = 'unknown') {
	const ic = getIntercom()
	if (!ic) {
		log('boot-identified-skipped', { reason, cause: 'no-intercom' })
		return
	}

	if (!user?.id) {
		log('boot-identified-skipped', { reason, cause: 'no-user-id' })
		return
	}

	if (!user.intercom_hash_web) {
		log('boot-identified-skipped', {
			reason,
			cause: 'missing-intercom-hash',
		})
		return
	}

	if (
		bootMode === 'identified' &&
		bootedUserId === user.id &&
		bootedUserHash === user.intercom_hash_web
	) {
		log('boot-identified-skipped', { reason, cause: 'duplicate' })
		return
	}

	const settings = applySettings(buildIdentifiedSettings(user))
	if (!settings) return

	if (bootMode === 'anonymous') {
		ic('hide')
		ic('update', settings)
		bootMode = 'identified'
		bootedUserId = user.id
		bootedUserHash = user.intercom_hash_web
		lastIdentifiedSettings = settings
		log('boot-identified-from-anonymous', { reason })
		return
	}

	shutdown('before-identified-boot')
	ic('boot', settings)
	bootMode = 'identified'
	bootedUserId = user.id
	bootedUserHash = user.intercom_hash_web
	lastIdentifiedSettings = settings
	log('boot-identified', { reason })
}

export function updateRoute() {
	const ic = getIntercom()
	if (!ic || bootMode === null) return

	if (bootMode === 'identified' && lastIdentifiedSettings) {
		ic('update', lastIdentifiedSettings)
		return
	}

	if (bootMode === 'anonymous') {
		const settings = applySettings({})
		if (settings) ic('update', settings)
	}
}

export function isReady() {
	return bootMode === 'identified' && !!lastIdentifiedSettings?.app_id
}

export default {
	getBaseSettings,
	applySettings,
	shutdown,
	bootAnonymous,
	bootIdentified,
	buildIdentifiedSettings,
	updateRoute,
	isReady,
}
